import React, { useMemo } from 'react';
import styled from 'styled-components';
import { colors, typography } from '../../theme/colors';

// Slim ribbon that shows date deltas between consecutive docs in a single
// chain. Visible only when one chain is selected (highlighted). Its longest
// segment auto-highlights — that's where the cycle leaked time.
const CycleTimelineRibbon = ({ chain, nodes }) => {
  const segments = useMemo(() => {
    if (!chain || !nodes) return [];
    const chainNodes = chain.nodeIds
      .map(id => nodes.find(n => n.id === id))
      .filter(n => n && n.docDate)
      .sort((a, b) => (a.docDate || '').localeCompare(b.docDate || ''));
    const out = [];
    for (let i = 1; i < chainNodes.length; i++) {
      const prev = chainNodes[i - 1];
      const cur = chainNodes[i];
      const days = Math.max(0, Math.round(
        (new Date(cur.docDate).getTime() - new Date(prev.docDate).getTime()) / (1000 * 60 * 60 * 24)
      ));
      out.push({ from: prev, to: cur, days });
    }
    return { sequence: chainNodes, segments: out };
  }, [chain, nodes]);

  if (!segments || !segments.sequence || segments.sequence.length < 2) {
    return null;
  }
  const { sequence, segments: segs } = segments;
  const totalDays = segs.reduce((s, x) => s + x.days, 0);
  const longestIdx = segs.reduce((maxI, s, i, arr) => s.days > arr[maxI].days ? i : maxI, 0);

  return (
    <Ribbon>
      <RibbonInner>
        {sequence.map((node, i) => (
          <React.Fragment key={node.id}>
            <Stop>
              <Dot />
              <StopLabel>
                <span>{node.tranTypeCode}</span>
                <strong>{node.docNumber}</strong>
              </StopLabel>
              <StopDate>{node.docDate}</StopDate>
            </Stop>
            {i < segs.length && (
              <Segment $longest={i === longestIdx} title={`${segs[i].days} days`}>
                <span>{segs[i].days}d</span>
              </Segment>
            )}
          </React.Fragment>
        ))}
      </RibbonInner>
      <Total>
        Total cycle: <strong>{totalDays}d</strong>
      </Total>
    </Ribbon>
  );
};

export default CycleTimelineRibbon;

const Ribbon = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  margin-top: 6px;
  background: white;
  border: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  border-radius: 6px;
  overflow-x: auto;
`;
const RibbonInner = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  flex: 1;
  min-width: 0;
`;
const Stop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 100px;
`;
const Dot = styled.span`
  width: 10px; height: 10px; border-radius: 50%;
  background: ${colors.primary.main};
  border: 2px solid white;
  box-shadow: 0 0 0 2px ${colors.primary.main}40;
`;
const StopLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  span { color: ${colors.text.secondary}; font-weight: 500; }
  strong { color: ${colors.text.primary}; font-weight: 700; }
`;
const StopDate = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${colors.text.secondary};
  font-family: ${typography.fontFamily.mono};
`;
const Segment = styled.div`
  flex: 1;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 2px solid ${p => p.$longest ? colors.error.main : colors.neutral?.[300] || '#cbd5e1'};
  margin: 0 -50px;
  position: relative;
  span {
    background: white;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 700;
    color: ${p => p.$longest ? colors.error.main : colors.text.primary};
    border: 1px solid ${p => p.$longest ? colors.error.main : colors.neutral?.[300] || '#cbd5e1'};
  }
`;
const Total = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text.secondary};
  white-space: nowrap;
  strong { color: ${colors.text.primary}; font-weight: 700; }
`;
